
import { z, type ZodType } from 'zod';
import { useStoredPreference } from '@/hooks/useStoredPreference';
import { viewChanges, viewSignature } from '@/components/viewComparison';

export const useSavedViews = <T,>(storageKey: string, schema: ZodType<T>, value: T, defaultValue: T = value, onApply: (value: T) => void) => {
    const viewSchema = z.array(z.object({ id: z.string(), name: z.string().min(1).max(60), state: schema })).max(20);
    const [views, setViews] = useStoredPreference(storageKey, viewSchema, []);
    const [selection, setSelection] = useStoredPreference(`${storageKey}:selection`, z.object({ id: z.string().nullable() }).nullable(), null);
    const parsed = schema.safeParse(value);
    const signature = parsed.success ? viewSignature(parsed.data) : '';
    const match = views.find(view => viewSignature(view.state) === signature);
    const selected = selection ? views.find(view => view.id === selection.id) : match;
    const changes = parsed.success ? viewChanges(parsed.data, schema.parse(selected?.state ?? defaultValue)) : [];
    const modified = changes.length > 0;

    const apply = (id: string): void => {
        const view = views.find(entry => entry.id === id);
        setSelection({ id: view?.id ?? null });
        onApply(view?.state ?? schema.parse(defaultValue));
    };
    const save = (name: string, mode: 'create' | 'update'): boolean => {
        const title = name.trim();
        if(!title || !parsed.success){ return false; }
        const previous = mode === 'update' ? selected : undefined;
        if(mode === 'update' && !previous){ return false; }
        if(views.some(view => view.id !== previous?.id && view.name.toLocaleLowerCase() === title.toLocaleLowerCase())){ return false; }
        if(!previous && views.length >= 20){ return false; }
        const id = previous?.id ?? crypto.randomUUID();
        setViews(current => [...current.filter(view => view.id !== id), { id, name: title, state: parsed.data }]);
        setSelection({ id });
        return true;
    };
    const remove = (): void => {
        if(!selected){ return; }
        setViews(current => current.filter(view => view.id !== selected.id));
        setSelection({ id: null });
        onApply(schema.parse(defaultValue));
    };
    return { views, selected, modified, changes, apply, save, remove, valid: parsed.success, full: views.length >= 20 };
};
